import { Select, SelectOptionType, SelectProps } from '@carrotfertility/carotene';
import { WidgetProps } from '@rjsf/core';
import React from 'react';
import { getHumanReadableErrorMessage } from './errorHandling';
import { CarrotJsonSchema, JsonSchemaFormInputProps } from './types';

type EnumOption = { value: string | number; label: string | number };
interface OverriddenWidgetProps extends WidgetProps {
    options: WidgetProps['options'] & { enumOptions?: EnumOption[] };
}

type SelectWidgetProps = OverriddenWidgetProps &
    JsonSchemaFormInputProps & {
        overrideSelectLabel: (fieldName: string, labelValue: string | number) => string | number;
    };

export function SelectWidget(props: SelectWidgetProps) {
    const {
        id,
        options,
        label,
        value,
        disabled,
        onChange,
        onBlur,
        onFocus,
        placeholder,
        rawErrors,
        required,
        schema,
        overrideTitle,
        overrideSelectLabel,
        overrideErrorMessage,
    } = props;

    const carrotSchema = schema as CarrotJsonSchema;

    function handleOnChange(changeValue: SelectOptionType) {
        onChange(changeValue.value);
    }

    const isError = rawErrors?.length > 0;
    let errorMessage;
    if (isError) {
        errorMessage = overrideErrorMessage
            ? overrideErrorMessage(carrotSchema.validationMessage, carrotSchema)
            : getHumanReadableErrorMessage(carrotSchema);
    }

    return (
        <Select
            {...{
                label: overrideTitle ? overrideTitle(label) : label,
                id,
                isRequired: required,
                isError,
                errorMessage,
                onChange: handleOnChange,
                disabled,
                placeholder,
                onFocus,
                onBlur,
                ...buildSelectOptionProps(carrotSchema.fieldName, options.enumOptions, value, overrideSelectLabel),
            }}
        />
    );
}

function buildSelectOptionProps(
    fieldName: string,
    enumOptions: EnumOption[],
    selectedId: string | number,
    overrideLabel: Pick<SelectWidgetProps, 'overrideSelectLabel'>['overrideSelectLabel']
): Pick<SelectProps, 'options' | 'value'> {
    const options: SelectOptionType[] = enumOptions?.map(
        (option) =>
            ({
                id: option.value,
                label: overrideLabel ? overrideLabel(fieldName, option.value) : option.label,
            } as SelectOptionType)
    );

    const selectedLabel = options?.find((option) => option.id === selectedId)?.label;
    const selectedOption: SelectOptionType = { id: selectedId, label: selectedLabel };

    return {
        options,
        value: selectedOption,
    };
}
